// src/components/WebsiteVisits/WebsiteVisitsTab.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import WebsiteVisitsTable from "./WebsiteVisitsTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import useWebsiteVisitsData from "./hooks/useWebsiteVisitsData";
import useWebsiteVisitFilters from "./hooks/useWebsiteVisitFilters";
import {
  cleanDisplay,
  displayOrDash,
  getStateDisplay,
  deriveCountry,
  getNaicsFields,
  getSector,
  getVisitCompanyKey,
  getGroupId,
  normalizeGuid,
  normalizeLocationForBRE,
} from "../../../utils/websiteVisitsHelpers";
import {
  Box,
  Stack,
  Alert,
  Snackbar,
  Card,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Zoom,
  Tooltip,
  ClickAwayListener,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import WebsiteVisitPanel from "./WebsiteVisitPanel";
import AccountContactsModal from "./AccountContactModal";
import VisitPageUrlsModal from "./VisitPageUrlsModal";
import { useAuth } from "../../../providers/AuthProvider";
import useModules from "../../../hooks/useModules";
import useWebsiteVisitSelection from "./useWebsiteVisitSelection";
import useWebsiteVisitActions from "./useWebsiteVisitActions";
import useWebsiteVisitModals from "./useWebsiteVisitModals";
import WebsiteVisitsHeader from "./WebsiteVisitsHeader";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { COUNTRY_MAP, normalizeCountryCode } from "../../../utils/Countries";
import { US_STATE_MAP, CANADA_PROVINCE_MAP } from "../../../utils/States";
import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import api from "../../../services/api/axios";

const PAGE_BG = "#f7f9fc";
const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";


const INITIAL_VISIBLE_ROWS = 100;
const ROW_INCREMENT = 100;

export default function WebsiteVisitsTab({ accountGuid }) {
  // 🔐 Auth: get client (org) state + name + GUID from /auth/me via AuthContext
  const { user } = useAuth();
  const { hasComponent } = useModules();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canBRE = hasComponent("bre.companies");
  const urlSince = searchParams.get("since");
  const urlUntil = searchParams.get("until");
  const initialSince = dayjs(urlSince).isValid() ? urlSince : undefined;
  const initialUntil = dayjs(urlUntil).isValid() ? urlUntil : undefined;

  // ✅ Group selected for right panel
  const [selectedGroup, setSelectedGroup] = useState(null);

  const clientState =
    (user?.organization_state || "").trim().toUpperCase() || null;
  const clientName = (user?.organization_name || "").trim();
  const orgGuid = (user?.organization_guid || "").trim();

  // UI filter state. Search/sector/location are sent to the backend; target
  // industry matching remains client-side because it is derived from org targets.
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 200);
  const [targetIndustryFilter, setTargetIndustryFilter] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);
  const [naicsFilter, setNaicsFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [countryFilter, setCountryFilter] = useState([]);
  const [leadScoreSort, setLeadScoreSort] = useState("desc");
  const [visibleRowLimit, setVisibleRowLimit] = useState(INITIAL_VISIBLE_ROWS);

  // Natural-language "AI search" — an alternative to the manual filters above.
  const [aiQuery, setAiQuery] = useState("");
  const [aiSearchActive, setAiSearchActive] = useState(false);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchError, setAiSearchError] = useState(null);
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiExplanationOpen, setAiExplanationOpen] = useState(false);

  const {
    normGuid,
    since,
    until,
    setSince,
    setUntil,
    items,
    loading,
    err,
    summary,
    burst,
    clientTargetIndustries,
    clientTargetIndustryCodes,
    scrollRef,
    trafficSources,
    trafficSourcesLoading,
    fetchTrafficSources,
    updateVisitTags,
    refreshGrouped,
  } = useWebsiteVisitsData({
    accountGuid,
    organizationGuid: orgGuid,
    initialSince,
    initialUntil,
    q: debouncedQ,
    industryFilter,
    countryFilter,
    stateFilter,
  });
  const effectiveItems = aiSearchActive ? aiSearchResult?.results || [] : items;

  const { sortedRows } = useWebsiteVisitFilters({
    items: effectiveItems,
    q: aiSearchActive ? "" : debouncedQ,
    since: aiSearchActive ? null : since,
    until: aiSearchActive ? null : until,
    targetIndustryFilter: aiSearchActive ? [] : targetIndustryFilter,
    industryFilter: aiSearchActive ? [] : industryFilter,
    naicsFilter: aiSearchActive ? [] : naicsFilter,
    stateFilter: aiSearchActive ? [] : stateFilter,
    countryFilter: aiSearchActive ? [] : countryFilter,
    clientState,
    clientTargetIndustries,
    clientTargetIndustryCodes,
    summary,
    burst,
    leadScoreSort: aiSearchActive ? null : leadScoreSort,
  });

  const filterLabels = useCallback((values = []) => {
    const labels = [];
    for (const value of values || []) {
      const raw = typeof value === "string" ? value : value?.label || value?.name;
      const text = String(raw || "").trim();
      if (text) labels.push(text);
    }
    return labels;
  }, []);

  const handleAiQueryChange = useCallback((e) => setAiQuery(e.target.value), []);

  const handleAiSearch = useCallback(async () => {
    const trimmed = aiQuery.trim();
    if (!trimmed || !normGuid) return;
    setAiSearchLoading(true);
    setAiSearchError(null);
    try {
      const res = await api.post(`/website-visits/${normGuid}/ai-search`, {
        query: trimmed,
        current_filters: {
          q: debouncedQ?.trim() || undefined,
          since,
          until,
          industries: filterLabels(industryFilter),
          countries: filterLabels(countryFilter),
          states: filterLabels(stateFilter),
        },
      });
      setAiSearchResult(res.data);
      setAiSearchActive(true);
      setAiPanelOpen(false);
      setAiExplanationOpen(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setAiSearchError(
        typeof detail === "string"
          ? detail
          : "AI search failed. Please try again, or use the manual filters above.",
      );
    } finally {
      setAiSearchLoading(false);
    }
  }, [aiQuery, normGuid, debouncedQ, since, until, industryFilter, countryFilter, stateFilter, filterLabels]);

  const handleClearAiSearch = useCallback(() => {
    setAiSearchActive(false);
    setAiSearchResult(null);
    setAiSearchError(null);
    setAiQuery("");
    setAiExplanationOpen(false);
  }, []);

  // =====================================================
  // Filter options computed from live data
  // =====================================================
  const ALL_TARGET_OPTION = "⭐ All Target Sectors";

  const targetIndustryOptions = useMemo(() => {
    const map = new Map();
    (clientTargetIndustries || []).forEach((t) => {
      const label = ((t.label || t.name) || "").trim();
      if (!label) return;
      if (!map.has(label)) map.set(label, { label, code: t.code });
    });
    const list = Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: ALL_TARGET_OPTION, isAllTarget: true }, ...list];
  }, [clientTargetIndustries]);

  const observedSectors = useMemo(() => {
    const rawOptions = items.map((visit) => {
      const row = visit?.representative || visit;
      return (
        cleanDisplay(row.sector) ||
        cleanDisplay(row?.naics_extra?.new_sector) ||
        cleanDisplay(row?.naics_extra?.sector)
      );
    });
    return rawOptions;
  }, [items]);
  const { options: sectorOptions } = useNaicsSectors(observedSectors);
  const industryOptions = sectorOptions.map((label) => ({ label }));

  const countryOptions = useMemo(() => {
    const set = new Set();
    items.forEach((v) => {
      const row = v?.representative || v;
      const raw = cleanDisplay(row.visitor_country);
      if (!raw) return;
      const normalized = normalizeCountryCode(raw);
      const fullName = COUNTRY_MAP[normalized] || raw;
      if (fullName) set.add(fullName);
    });
    return [...set].sort();
  }, [items]);

  const stateOptions = useMemo(() => {
    const set = new Set();
    items.forEach((v) => {
      const row = v?.representative || v;
      const rawCountry = cleanDisplay(row.visitor_country);
      const normalized = normalizeCountryCode(rawCountry);
      const fullCountry = COUNTRY_MAP[normalized] || rawCountry;
      if (!countryFilter.includes(fullCountry)) return;
      const rawState = cleanDisplay(row.visitor_state);
      if (!rawState) return;
      let fullState = rawState;
      if (fullCountry === "United States") fullState = US_STATE_MAP[rawState] || rawState;
      if (fullCountry === "Canada") fullState = CANADA_PROVINCE_MAP[rawState] || rawState;
      set.add(fullState);
    });
    return [...set].sort();
  }, [items, countryFilter]);

  const naicsOptions = useMemo(() => {
    const seen = new Set();
    items.forEach((visit) => {
      const { naicsName } = getNaicsFields(visit?.representative || visit);
      if (naicsName) seen.add(naicsName);
    });
    return [...seen].sort();
  }, [items]);

  // Snackbar for "Save to Prospects"
  const [saveSnackOpen, setSaveSnackOpen] = useState(false);
  const [saveSnackMsg, setSaveSnackMsg] = useState("");
  const [saveSnackSeverity, setSaveSnackSeverity] = useState("success");
  const memoizedSelectionResetKey = useMemo(
    () =>
      JSON.stringify({
        targetIndustryFilter,
        industryFilter,
        naicsFilter,
        stateFilter,
        countryFilter,
        q: debouncedQ,
      }),
    [
      targetIndustryFilter,
      industryFilter,
      naicsFilter,
      stateFilter,
      countryFilter,
      debouncedQ,
    ],
  );
  const showSnackbar = useCallback((severity, message) => {
    setSaveSnackSeverity(severity);
    setSaveSnackMsg(message);
    setSaveSnackOpen(true);
  }, []);

  const {
    selectedIds,
    selectedCount,
    isAllSelected,
    isSomeSelected,
    handleToggleOne,
    handleToggleAll,
  } = useWebsiteVisitSelection({
    sortedRows,
    getGroupId,
    resetKey: memoizedSelectionResetKey,
  });

  const {
    contactsModalOpen,
    contactsVisitorId,
    contactsCompanyKey,
    contactsCompanyName,
    contactsPageUrl,
    pageModalOpen,
    pageModalData,
    handleOpenContactsModal: openContactsModal,
    handleCloseContactsModal,
    handleOpenPagesModal,
    handleClosePagesModal,
  } = useWebsiteVisitModals({ cleanDisplay });

  const {
    handleExportSelected,
    handleSaveSelectedVisit,
    handleSaveSelectedBRE,
  } = useWebsiteVisitActions({
    sortedRows,
    selectedIds,
    getGroupId,
    cleanDisplay,
    getNaicsFields,
    getStateDisplay,
    deriveCountry,
    getSector,
    normalizeGuid,
    normalizeLocationForBRE,
    showSnackbar,
  });

  // Keep selectedGroup valid when filters/data change
  useEffect(() => {
    if (!selectedGroup) {
      return;
    }

    const matchingGroup = sortedRows.find(
      (g) => getGroupId(g) === getGroupId(selectedGroup),
    );

    if (matchingGroup) {
      if (matchingGroup !== selectedGroup) {
        setSelectedGroup(matchingGroup);
      }
      return;
    }

    setSelectedGroup(null);
  }, [sortedRows, selectedGroup]);

  // Fetch traffic sources when selected company changes
  useEffect(() => {
    if (!selectedGroup) {
      fetchTrafficSources(null);
      return;
    }

    const companyKey = getVisitCompanyKey(selectedGroup.representative);
    if (!companyKey) {
      fetchTrafficSources(null);
      return;
    }

    fetchTrafficSources(companyKey);
  }, [selectedGroup, fetchTrafficSources]);

  // === Multi-select helpers ===
  const handleSelect = useCallback((group) => {
    setSelectedGroup(group);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedGroup(null);
  }, []);

  // When "View Contacts" is clicked in CompanyDetailsPanel
  const handleOpenContactsModal = useCallback(
    () => {
      openContactsModal(selectedGroup);
    },
    [openContactsModal, selectedGroup],
  );

  // Toggle lead score sorting
  const handleToggleLeadScoreSort = useCallback(() => {
    setLeadScoreSort((prev) =>
      prev === "desc" ? "asc" : prev === "asc" ? null : "desc",
    );
  }, []);

  useEffect(() => {
    setVisibleRowLimit(INITIAL_VISIBLE_ROWS);
  }, [memoizedSelectionResetKey, leadScoreSort, items]);

  const visibleRows = useMemo(
    () => sortedRows.slice(0, visibleRowLimit),
    [sortedRows, visibleRowLimit],
  );

  const handleTableScroll = useCallback((event) => {
    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom > 240) return;

    setVisibleRowLimit((prev) =>
      prev >= sortedRows.length
        ? prev
        : Math.min(prev + ROW_INCREMENT, sortedRows.length),
    );
  }, [sortedRows.length]);

  // 🔴 If there is truly no GUID (neither prop nor org), show the warning
  if (!normGuid) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          ❗ Your organization does not have a Dynamics Website Account GUID
          configured. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <WebsiteVisitsHeader
        q={q}
        setQ={setQ}
        navigate={navigate}
        selectedCount={selectedCount}
        handleSaveSelectedVisit={handleSaveSelectedVisit}
        handleSaveSelectedBRE={handleSaveSelectedBRE}
        handleExportSelected={handleExportSelected}
        loading={loading}
        canAccessBRE={canBRE}
        since={since}
        until={until}
        onSinceChange={setSince}
        onUntilChange={setUntil}
      />

      <Box sx={{ px: { xs: 2, md: 3 }, mt: 2 }}>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Request failed{err.status ? ` (${err.status})` : ""}:{" "}
            {String(err.detail)}
          </Alert>
        )}

        {/* Compact AI result summary; the explanation is available in the dialog. */}
        {aiSearchActive && aiSearchResult && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: "#eef2f8",
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, color: BRAND_BLUE, fontWeight: 700 }}>
                Ask AI: “{aiSearchResult.original_query}”
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => setAiExplanationOpen(true)}
                sx={{ backgroundColor: BRAND_BLUE }}
              >
                View AI explanation
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAiSearch}
                disabled={aiSearchLoading}
                sx={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        )}

        {aiSearchActive && aiSearchResult && (
          <Dialog
            open={aiExplanationOpen}
            onClose={() => setAiExplanationOpen(false)}
            aria-labelledby="website-visits-ai-explanation-title"
            fullWidth
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 24px 70px rgba(0, 28, 61, 0.24)",
              },
            }}
          >
            <DialogTitle sx={{ p: 0 }}>
              <Box
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  py: 2.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.75,
                  color: "#ffffff",
                  background: `linear-gradient(120deg, ${BRAND_BLUE} 0%, #145a83 72%, ${BRAND_GREEN} 150%)`,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.16)",
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", opacity: 0.8 }}>
                    WEBSITE VISITORS · ASK AI
                  </Typography>
                  <Typography id="website-visits-ai-explanation-title" sx={{ fontSize: 22, fontWeight: 750, lineHeight: 1.25, mt: 0.25 }}>
                    Visitor analysis
                  </Typography>
                  <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.75, overflowWrap: "anywhere" }}>
                    “{aiSearchResult.original_query}”
                  </Typography>
                </Box>
                <IconButton
                  aria-label="Close AI analysis"
                  onClick={() => setAiExplanationOpen(false)}
                  sx={{ color: "#ffffff", mt: -0.5, mr: -0.5, "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.16)" } }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3, backgroundColor: "#f7f9fc" }}>
              {aiSearchResult.answer && (
                <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: "#ffffff", border: "1px solid #dfe7f0", borderLeft: `4px solid ${BRAND_GREEN}` }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: BRAND_GREEN, mb: 1 }}>
                    AI SUMMARY
                  </Typography>
                  <Typography sx={{ fontSize: 15, lineHeight: 1.7, color: "#233449", whiteSpace: "pre-line" }}>
                    {aiSearchResult.answer}
                  </Typography>
                </Box>
              )}
              {aiSearchResult.results?.some((item) => item.ai_reason) && (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 750, color: BRAND_BLUE, mb: 1.5 }}>
                    Companies to review
                  </Typography>
                  {aiSearchResult.results.map((item, index) => (
                    <Box
                      key={item.representative?.id || index}
                      sx={{ p: 2, mb: 1, borderRadius: 2, border: "1px solid #dfe7f0", backgroundColor: "#ffffff" }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, backgroundColor: "#eaf2dc", color: BRAND_GREEN, fontSize: 12, fontWeight: 800 }}>
                          {index + 1}
                        </Box>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Stack direction="row" alignItems="center" sx={{ gap: 1, flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 750, color: BRAND_BLUE, overflowWrap: "anywhere" }}>
                              {item.representative?.visitor_company_name || "Company"}
                            </Typography>
                            {item.leadScore != null && (
                              <Chip label={`Engagement ${item.leadScore}`} size="small" sx={{ height: 22, fontSize: 11, color: BRAND_GREEN, backgroundColor: "#f0f5e7", fontWeight: 700 }} />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: "#46566a", mt: 0.5 }}>
                            {item.ai_reason}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
              {Object.keys(aiSearchResult.interpreted_filters || {}).length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#66768a", mb: 1, letterSpacing: "0.08em" }}>
                    INTERPRETED FILTERS
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {Object.entries(aiSearchResult.interpreted_filters).map(([key, value]) => (
                      <Chip
                        key={key}
                        size="small"
                        label={`${key.replace(/_/g, " ")}: ${
                          Array.isArray(value) ? value.join(", ") : String(value)
                        }`}
                        sx={{ backgroundColor: "#e5eef7", color: BRAND_BLUE, fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {aiSearchResult.warnings?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {aiSearchResult.warnings.map((warning, idx) => (
                    <Alert key={idx} severity="warning" sx={{ mb: 1, fontSize: 12 }}>{warning}</Alert>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.75, borderTop: "1px solid #e5eaf0", backgroundColor: "#ffffff" }}>
              <Button onClick={() => setAiExplanationOpen(false)} variant="contained" sx={{ backgroundColor: BRAND_BLUE, px: 2.5, borderRadius: 1.5 }}>
                Done
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Table (left) + Panel (right) */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{
            alignItems: "stretch",
            minHeight: { xs: "auto", lg: "82vh" },
            maxHeight: { xs: "auto", lg: "82vh" },
          }}
        >
          <Box
            sx={{
              p: 0,
              flexBasis: { xs: "100%", lg: selectedGroup ? "55%" : "100%" },
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <WebsiteVisitsTable
              sortedRows={visibleRows}
              totalRowCount={sortedRows.length}
              selectedGroup={selectedGroup}
              onSelectGroup={handleSelect}
              selectedIds={selectedIds}
              onToggleOne={handleToggleOne}
              onToggleAll={handleToggleAll}
              isAllSelected={isAllSelected}
              isSomeSelected={isSomeSelected}
              leadScoreSort={aiSearchActive ? null : leadScoreSort}
              onToggleLeadScoreSort={handleToggleLeadScoreSort}
              getGroupId={getGroupId}
              getStateDisplay={getStateDisplay}
              deriveCountry={deriveCountry}
              getNaicsFields={getNaicsFields}
              getSector={getSector}
              cleanDisplay={cleanDisplay}
              displayOrDash={displayOrDash}
              clientName={clientName}
              loading={loading}
              err={err}
              normGuid={normGuid}
              handleScroll={handleTableScroll}
              scrollRef={scrollRef}
              onVisitTagsUpdated={updateVisitTags}
              targetIndustryFilter={targetIndustryFilter}
              onTargetIndustryChange={setTargetIndustryFilter}
              industryFilter={industryFilter}
              onIndustryChange={setIndustryFilter}
              countryFilter={countryFilter}
              onCountryChange={setCountryFilter}
              stateFilter={stateFilter}
              onStateChange={setStateFilter}
              targetIndustryOptions={targetIndustryOptions}
              industryOptions={industryOptions}
              countryOptions={countryOptions}
              stateOptions={stateOptions}
              naicsFilter={naicsFilter}
              onNaicsChange={setNaicsFilter}
              naicsOptions={naicsOptions}
            />
          </Box>

          {selectedGroup && (
            <Box
              sx={{
                flexBasis: { xs: "100%", lg: "45%" },
                flexGrow: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <WebsiteVisitPanel
                group={{
                  ...selectedGroup,
                  onOpenPages: handleOpenPagesModal,
                }}
                summary={summary}
                burst={burst}
                companyKey={getVisitCompanyKey(selectedGroup.representative)}
                trafficSources={trafficSources}
                trafficSourcesLoading={trafficSourcesLoading}
                onOpenContactsModal={handleOpenContactsModal}
                deriveCountry={deriveCountry}
                onClose={handleClosePanel}
                onRefreshVisitorDetails={refreshGrouped}
                showSnackbar={showSnackbar}
              />
            </Box>
          )}
        </Stack>
      </Box>

      <AccountContactsModal
        open={contactsModalOpen}
        onClose={handleCloseContactsModal}
        visitorId={contactsVisitorId}
        companyKey={contactsCompanyKey}
        accountGuid={normGuid}
        companyName={contactsCompanyName}
        pageUrl={contactsPageUrl}
      />

      <VisitPageUrlsModal
        open={pageModalOpen}
        onClose={handleClosePagesModal}
        companyName={pageModalData.companyName}
        visitDate={pageModalData.visitDate}
        pages={pageModalData.pages}
        visitDurationSeconds={pageModalData.durationSeconds}
      />

      <Snackbar
        open={saveSnackOpen}
        autoHideDuration={4000}
        onClose={() => setSaveSnackOpen(false)}
      >
        <Alert
          severity={saveSnackSeverity}
          onClose={() => setSaveSnackOpen(false)}
        >
          {saveSnackMsg}
        </Alert>
      </Snackbar>

      {/* ===== Floating AI Search ===== */}
      <ClickAwayListener onClickAway={() => setAiPanelOpen(false)}>
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 32 },
            right: { xs: 20, md: 32 },
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1.5,
          }}
        >
          <Zoom in={aiPanelOpen} unmountOnExit>
            <Card
              elevation={8}
              sx={{
                width: 380,
                maxWidth: "calc(100vw - 40px)",
                borderRadius: 3,
                p: 2,
                transformOrigin: "bottom right",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                <AutoAwesomeIcon sx={{ color: BRAND_GREEN, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: 14.5 }}>
                  Ask AI to find visitors
                </Typography>
              </Box>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                placeholder="e.g. Highly engaged manufacturers from Ontario that visited our pricing page"
                value={aiQuery}
                onChange={handleAiQueryChange}
                disabled={aiSearchLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAiSearch();
                  }
                }}
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: 1.5,
                  "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                }}
              />
              {aiSearchError && (
                <Typography sx={{ mt: 1, fontSize: 12.5, color: "#c62828" }}>
                  {aiSearchError}
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 1, mt: 1.5, justifyContent: "flex-end" }}>
                {aiSearchActive && (
                  <Button
                    size="small"
                    onClick={handleClearAiSearch}
                    disabled={aiSearchLoading}
                    sx={{ color: BRAND_BLUE }}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAiSearch}
                  disabled={aiSearchLoading || !aiQuery.trim()}
                  startIcon={
                    aiSearchLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    backgroundColor: BRAND_GREEN,
                    "&:hover": { backgroundColor: "#547011" },
                  }}
                >
                  {aiSearchLoading ? "Searching…" : "Search"}
                </Button>
              </Box>
            </Card>
          </Zoom>

          <Tooltip title={aiPanelOpen ? "" : "Ask AI to find visitors"} placement="left">
            <Box
              component="button"
              type="button"
              onClick={() => setAiPanelOpen((prev) => !prev)}
              sx={{
                position: "relative",
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                background: `linear-gradient(135deg, ${BRAND_GREEN}, ${BRAND_BLUE})`,
                color: "#ffffff",
                display: "grid",
                placeItems: "center",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.28)",
                transition: "transform 0.15s ease",
                "&:hover": { transform: "scale(1.06)" },
              }}
            >
              {aiPanelOpen ? <CloseIcon /> : <AutoAwesomeIcon />}
              {aiSearchActive && !aiPanelOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#2e7d32",
                    border: "2px solid #ffffff",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
      </ClickAwayListener>
    </Box>
  );
}
